import * as React from 'react';
import {
  ErrorCode,
  type DropzoneOptions,
  type FileError,
} from 'react-dropzone';
import clsx from 'clsx';
import {
  type ComponentsOverrides,
  type ComponentsProps,
  useThemeProps,
} from '@mui/material';
import useId from '@mui/utils/useId';

import { type BoxProps } from 'src/components/Box';
import type { FileListItemProps } from 'src/components/FileListItem/FileListItem';
import { type StackProps } from 'src/components/Stack';

import { useFileUploaderAlertErrors } from './hooks/useFileUploaderAlertErrors';
import { FileUploaderAlert } from './slots/FileUploaderAlert';
import { FileUploaderFieldsetRoot } from './slots/FileUploaderFieldset';
import { FileUploaderLegend } from './slots/FileUploaderLegend';
import {
  FileUploaderDropzone,
  type FileUploaderDropzoneOwnProps,
} from './slots/FileUploaderDropzone';
import { FileUploaderFileList } from './slots/FileUploaderFileList';
import fileUploaderClasses, {
  type FileUploaderClassKey,
} from './fileUploaderClasses';
import { FileLike, FileModel } from './fileModel';
import { buildFileUploaderDescriptionText } from './buildFileUploaderDescriptionText';
import { ButtonProps } from 'src/components/Button';

declare module '@mui/material/styles' {
  interface ComponentNameToClassKey {
    UnifyFileUploader: FileUploaderClassKey;
  }

  interface ComponentsPropsList {
    UnifyFileUploader: FileUploaderProps;
  }

  interface Components {
    UnifyFileUploader?: {
      defaultProps?: ComponentsProps['UnifyFileUploader'];
      styleOverrides?: ComponentsOverrides<Theme>['UnifyFileUploader'];
    };
  }
}

/**
 * The public prop API for the {@link FileUploader} component.
 */
export type FileUploaderProps = {
  /**
   * The list of data models used to define the files displayed by the component,
   * along with error and upload progress metadata about them.
   *
   * Use this prop alongside {@link onChange} to control the file values displayed by the FileUploader.
   *
   * **Required.**
   *
   * See {@link FileUploader} for a detailed implementation example.
   */
  fileValues: FileModel[];

  /**
   * A callback that fires when the user drops files or chooses files from the system file picker to upload.
   *
   * **Required.**
   *
   * See {@link FileUploader} for a detailed implementation example.
   *
   * @see fileValues
   *
   * @param fileModels A list of `FileModel`s containing the file objects, along with a list of
   * `FileError`s for any files that were "rejected" based on restrictions specified via props like
   * {@link acceptedMimeTypes} and {@link maxSizeInBytes}.
   *
   * @param defaultAlertErrors A list of errors that occurred because of the rejection of one or more files that
   * display in a "top level" `Alert` by default instead of within an individual file item. For example,
   * the error that is displayed when the length of {@link fileValues} exceeds the value of the
   * {@link maxNumberOfFiles} prop. The message displayed in this "top level" alert can be customized
   * by setting the {@link alertErrors} prop.
   */
  onChange: (fileModels: FileModel[], defaultAlertErrors: FileError[]) => void;

  /**
   * The title that serves as a label for the `FileUploader`.
   *
   * While **this prop should never be empty**, it can be visually hidden by setting
   * {@link hideTitleText} to true.
   *
   * @see descriptionText
   *
   * @default 'Upload Files'
   */
  titleText?: string;

  /**
   * Whether the {@link titleText} should be visually hidden from the user.
   *
   * @default false
   */
  hideTitleText?: boolean;

  /**
   * Descriptive text that helps the user understand what they can upload.
   *
   * While **this prop should never be empty**, it can be visually hidden by setting
   * {@link hideDescriptionText} to true.
   *
   * @see titleText
   *
   * @default A string that informs the user what they can upload based on the value
   * of the {@link acceptedMimeTypes}, {@link maxNumberOfFiles} and
   * {@link maxSizeInBytes} props.
   */
  descriptionText?: string;

  /**
   * Whether the {@link descriptionText} should be visually hidden from the user.
   *
   * @default false
   */
  hideDescriptionText?: boolean;

  /**
   * Whether the file upload capabilities of this component should be disabled.
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * The maximum number of files that this `FileUpload` instance can upload.
   *
   * When set, a "top level" error will be displayed by default within an `Alert`
   * beneath the dropzone.
   *
   * Optionally, you can use the {@link onChange} arguments and {@link alertError}
   * prop to customize the message displayed when the user tries to upload too many
   * files.
   *
   * TODO (FED-3995): Add more thorough example(s) of single file upload mode when this prop is set to 1.
   */
  maxNumberOfFiles?: DropzoneOptions['maxFiles'];

  /**
   * The file types that this `FileUpload` instance accepts.
   *
   * When set, files that the user tries to upload that have mime types not
   * found in this object will be rejected, causing their {@link FileModel}
   * to contain `errors` when passed to you via the {@link onChange} prop.
   *
   * This is an object keyed by mime type strings with a list of file type
   * extensions that are possible for that mime type.
   *
   * ### Example
   * ```ts
   * const acceptedMimeTypes = {
   *   'image/jpeg': ['.jpeg', '.jpg'],
   *   'image/png': ['.png'],
   * } satisfies FileUploaderProps['acceptedMimeTypes'];
   * ```
   *
   * @default {}
   */
  acceptedMimeTypes?: DropzoneOptions['accept'];

  /**
   * The minimum file size accepted by this `FileUpload` instance.
   *
   * When set, files that the user tries to upload that are smaller than this
   * will be rejected, causing their {@link FileModel} to contain `errors`
   * when passed to you via the {@link onChange} prop.
   *
   * @see maxSizeInBytes
   */
  minSizeInBytes?: DropzoneOptions['minSize'];

  /**
   * The maximum file size accepted by this `FileUpload` instance.
   *
   * When set, files that the user tries to upload that are larger than this
   * will be rejected, causing their {@link FileModel} to contain `errors`
   * when passed to you via the {@link onChange} prop.
   *
   * @see minSizeInBytes
   */
  maxSizeInBytes?: DropzoneOptions['maxSize'];

  /**
   * An optional list of custom "top level" errors that will be displayed an `Alert` beneath the dropzone.
   *
   * Default errors that will be displayed in this `Alert` are passed via the {@link onChange} prop arguments.
   *
   * > **NOTE** Setting this prop to `[]` will result in default "top level" error messaging behavior being disabled
   * in the component. If you don't want custom error message(s) displayed, set to `undefined`.
   *
   * ### Example
   * ```ts
   * import * as React from 'react';
   * import { FileUploader, type FileUploaderProps } from '@workiva/unify';
   *
   * function ControlledFileUploader(
   *   props: Omit<FileUploaderProps, 'fileValues'|'alertErrors'> & {
   *     initialFileValues?: FileUploaderProps['fileValues'];
   *     initialAlertErrors?: FileUploaderProps['alertErrors'];
   *   }
   * ): React.ReactElement {
   *   const {
   *     initialFileValues,
   *     initialAlertErrors,
   *     onChange: onChangeProp,
   *     setFileValues: setFileValuesProp,
   *     ...rest
   *   } = props;
   *   const [fileValues, setFileValues] = React.useState(initialFileValues);
   *   const [alertErrors, setAlertErrors] = React.useState(initialAlertErrors);
   *   const handleChange = React.useCallback<FileUploaderProps['onChange']>(
   *     (fileModels, alertErrors) => {
   *       setFileValues(newFileValues);
   *       const customAlertErrors = someCustomFnThatCustomizesAlertErrors(alertErrors);
   *       setAlertErrors(customAlertErrors)
   *       onChangeProp?.(newFileValues, customAlertErrors);
   *     },
   *     [onChangeProp, someCustomFnThatCustomizesAlertErrors]
   *   );
   *   return (
   *     <FileUploader
   *       fileValues={fileValues}
   *       alertErrors={alertErrors}
   *       onChange={handleChange}
   *       {...rest}
   *     />
   *   );
   * }
   * ```
   */
  alertErrors?: FileError[];

  /**
   * Whether the list of files that have been uploaded should be hidden from the user.
   *
   * > **NOTE** Setting this to true when {@link maxNumberOfFiles} is 1 will not have any effect on the component.
   *
   * @see maxFileListItemsToDisplay
   *
   * @default false
   */
  hideFileList?: boolean;

  /**
   * The maximum number of uploaded files that should be displayed to the user beneath the dropzone by default.
   *
   * Set to -1 to display a `FileListItem` for every file in {@link fileValues}.
   * It is strongly recommended that you set {@link maxFileListContainerHeight}
   * when doing this so that the list scrolls vertically to reveal all of the files.
   *
   * @default 3
   */
  maxFileListItemsToDisplay?: number;

  /**
   * The CSS `max-height` value applied to the element containing the list of files.
   *
   * This will cause the list to vertically scroll when the height of the
   * `FileListItem`s displayed for each of the {@link fileValues} exceeds
   * the value provided.
   *
   * > **Do not set to a zero value**
   * >
   * > If you want to hide the file list altogether, set {@link hideFileList} to true.
   *
   * @see maxFileListItemsToDisplay
   */
  maxFileListContainerHeight?: string | number;

  /**
   * Whether the file size should be displayed for each uploaded file.
   *
   * @default false
   */
  showFileSize?: boolean;

  /**
   * An optional callback that fires when and uploaded file is requested to be removed by the user.
   */
  onRemoveFileListItem?: (file: FileModel['file']) => void;

  /**
   * An optional callback that can be used to render completely custom UI to represent uploaded files.
   *
   * ### Example
   * A FileUploader that renders a Chip for each uploaded file:
   * ```tsx
   * import * as React from 'react';
   * import {
   *   Chip,
   *   type ChipProps,
   *   FileUploader,
   *   type FileUploaderProps,
   *   type FileListItemProps,
   *   getFileStatusFromModel
   * } from '@workiva/unify';
   *
   * function FileUploaderWithCustomFileItemRendering(
   *   props: Omit<FileUploaderProps, 'renderFileListItem'>
   * ): React.ReactElement {
   *   return (
   *     <FileUploader
   *       {...props}
   *       renderFileListItem={(itemProps) => {
   *         return <FileChipItem {...itemProps} />;
   *       }}
   *     />
   *   );
   * }
   *
   * type FileChipItemProps = FileListItemProps & ChipProps;
   *
   * function FileChipItem(props: FileChipItemProps): React.ReactElement | null {
   *   const { file, errors, uploadPercentComplete, ...rest } = props;
   *   return (
   *     <Chip
   *       {...rest}
   *       label={file.name}
   *       disabled={uploadPercentComplete !== 100}
   *       icon={
   *         <FileListItemIcon
   *           fileStatus={getFileStatusFromModel({
   *             file,
   *             errors,
   *             uploadPercentComplete,
   *           })}
   *           fileType={file.type}
   *           uploadPercentComplete={uploadPercentComplete ?? 100}
   *           slotProps={{ CircularProgress: { size: 20 } }}
   *         />
   *       }
   *     />
   *   );
   * }
   * ```
   *
   * @param props The props that should be passed to the item.
   */
  renderFileListItem?: (props: FileListItemProps) => React.ReactElement | null;

  /**
   * A callback that can be used to return a custom list of `MenuItems` that will be displayed
   * in a dropdown menu triggered by an `IconButton` in the secondary actions area of each
   * rendered `FileListItem` once the upload is complete.
   *
   * Be sure to include the `removeFileMenuItemElement` passed to the callback as an argument
   * as shown in the example below to ensure the user can still remove the file if they need to.
   * _(The `removeFileMenuItemElement` is the one that calls the {@link onRemoveFileListItem} prop callback)_
   *
   * @param removeFileMenuItemElement a pre-defined MenuItem element that allows the user to remove the file.
   * @param file the file object that this menu acts upon.
   *
   * @example
   * ```tsx
   * import * as React from 'react';
   * import { FileUploader, type FileUploaderProps, MenuItem } from '@workiva/unify';
   *
   * function FileUploaderWithCustomFileActionMenus(
   *   props: Omit<FileUploaderProps, 'fileValues'> & {
   *     initialFileValues?: FileUploaderProps['fileValues'];
   *   }
   * ): React.ReactElement {
   *   const {
   *     initialFileValues,
   *     initialAlertErrors,
   *     onChange: onChangeProp,
   *     setFileValues: setFileValuesProp,
   *     ...rest
   *   } = props;
   *   const [fileValues, setFileValues] = React.useState(initialFileValues);
   *   const handleChange = React.useCallback<FileUploaderProps['onChange']>(
   *     (fileModels, alertErrors) => {
   *       setFileValues(newFileValues);
   *       onChangeProp?.(newFileValues, customAlertErrors);
   *     },
   *     [onChangeProp]
   *   );
   *   return (
   *     <FileUploader
   *       fileValues={fileValues}
   *       onChange={handleChange}
   *       renderFileListItemActionMenuItems={(removeFileMenuItemElement, file) => ([
   *         removeFileMenuItemElement,
   *         <MenuItem key={1} onClick={someCustomAction}>Some Custom Action For {file.name}</MenuItem>
   *         <MenuItem key={2} onClick={someCustomAction2}>Some Other Custom Action For {file.name}</MenuItem>
   *       ])}
   *       {...rest}
   *     />
   *   );
   * }
   * ```
   */
  renderFileListItemActionMenuItems?: (
    removeFileMenuItemElement: React.ReactElement,
    file: FileLike
  ) => React.ReactElement[];

  /**
   * An escape hatch to allow arbitrary props to be passed to the various slots rendered by the `FileUploader` component.
   *
   * **Recommended for advanced use-cases only**
   */
  slotProps?: {
    fieldset?: React.HTMLAttributes<HTMLFieldSetElement> & Pick<BoxProps, 'sx'>;
    legend?: React.HTMLAttributes<HTMLLegendElement> & Pick<BoxProps, 'sx'>;
    fileList?: React.HTMLAttributes<HTMLElement> &
      Partial<Omit<StackProps, 'ref'>> & {
        actionFooterButtons?: {
          showMoreLessButtonVariant?: ButtonProps['variant'];
          clearAllButtonVariant?: ButtonProps['variant'];
          showMoreLessButton?: boolean;
          showClearAllButton?: boolean;
        };
      };
    dropzone?: Omit<React.HTMLAttributes<HTMLButtonElement>, 'onChange'> &
      Pick<BoxProps, 'sx'> &
      Pick<FileUploaderDropzoneOwnProps, 'size'>;
  };
};

/**
 * A component that enables users to upload files from their device through drag-and-drop or file system dialog interactions.
 *
 * This component **must be controlled by state from your application** via the
 * `fileValues` and `onChange` props as shown in the example below.
 *
 * ### Example
 * ```ts
 * import * as React from 'react';
 * import { FileUploader, type FileUploaderProps } from '@workiva/unify';
 *
 * function ControlledFileUploader(
 *   props: Omit<FileUploaderProps, 'fileValues'> & {
 *     initialFileValues?: FileUploaderProps['fileValues'];
 *   }
 * ): React.ReactElement {
 *   const {
 *     initialFileValues,
 *     onChange: onChangeProp,
 *     setFileValues: setFileValuesProp,
 *     ...rest
 *   } = props;
 *   const [fileValues, setFileValues] = React.useState(initialFileValues);
 *   const handleChange = React.useCallback<FileUploaderProps['onChange']>(
 *     (fileModels, alertErrors) => {
 *       setFileValues(fileModels);
 *       onChangeProp?.(fileModels, alertErrors);
 *     },
 *     [onChangeProp]
 *   );
 *   return (
 *     <FileUploader fileValues={fileValues} onChange={handleChange} {...rest} />
 *   );
 * }
 * ```
 */
export const FileUploader = React.forwardRef<
  HTMLFieldSetElement,
  FileUploaderProps
>(function FileUploader(inProps, forwardedRef) {
  const props = useThemeProps({
    props: inProps,
    name: 'UnifyFileUploader',
  });
  const {
    // ------- Props Consumed Directly ------- //
    fileValues: fileValuesProp,
    alertErrors: alertErrorsProp,
    hideFileList: hideFileListProp = false,
    slotProps = {},

    // ------- Props forwarded to Legend Slot ------- //
    titleText = 'Upload Files',
    hideTitleText = false,
    descriptionText: descriptionTextProp,
    hideDescriptionText: hideDescriptionTextProp = false,

    // ------- Props forwarded to FileList Slot ------- //
    renderFileListItem,
    showFileSize,
    onRemoveFileListItem,
    maxFileListItemsToDisplay,
    renderFileListItemActionMenuItems,
    maxFileListContainerHeight,

    // ------- Props forwarded to DropZone Slot ------- //
    disabled = false,
    onChange,
    acceptedMimeTypes = {},
    minSizeInBytes,
    maxSizeInBytes,
    maxNumberOfFiles = 0,
  } = props;
  const rootId = useId();

  const descriptionText = buildFileUploaderDescriptionText({
    descriptionText: descriptionTextProp,
    acceptedMimeTypes,
    maxSizeInBytes,
    maxNumberOfFiles,
  });
  const hideDescriptionText =
    hideDescriptionTextProp || descriptionText.length === 0;
  const descriptionTextId = !hideDescriptionText ? `${rootId}-desc` : undefined;

  const { getDefaultAlertErrors } = useFileUploaderAlertErrors({
    acceptedMimeTypes,
    maxNumberOfFiles,
    minSizeInBytes,
    maxSizeInBytes,
  });

  const alertErrors = alertErrorsProp ?? getDefaultAlertErrors(fileValuesProp);

  const lastFileValues = React.useRef(fileValuesProp);
  React.useEffect(() => {
    lastFileValues.current = fileValuesProp;
  }, [fileValuesProp]);

  const alertErrorShownInSingleFileMode =
    maxNumberOfFiles === 1 &&
    !!alertErrors.some((err) => {
      const codesToDisplayAlertErrorsFor: string[] = [
        ErrorCode.TooManyFiles,
        ErrorCode.FileInvalidType,
      ];
      return codesToDisplayAlertErrorsFor.includes(err.code);
    });
  const shouldHideFileList =
    hideFileListProp || disabled || alertErrorShownInSingleFileMode;
  const showDropzone =
    disabled ||
    alertErrorShownInSingleFileMode ||
    !(maxNumberOfFiles === 1 && fileValuesProp.length > 0);

  if (disabled && fileValuesProp.length > 0) {
    console.warn(
      'UnifyFileUploader: The component should not be in a disabled state when files are present.'
    );
  }

  const handleChange = React.useCallback<
    FileUploaderDropzoneOwnProps['onChange']
  >(
    (newFileModels) => {
      const latestFileModels = [...newFileModels, ...lastFileValues.current];
      const latestAlertErrors = getDefaultAlertErrors(latestFileModels);

      onChange(latestFileModels, latestAlertErrors);
    },
    [getDefaultAlertErrors, onChange]
  );

  const handleRemoveFileListItem = React.useCallback<
    NonNullable<FileUploaderProps['onRemoveFileListItem']>
  >(
    (removedFile) => {
      const latestFileModels = lastFileValues.current.filter(
        (model) => model.file !== removedFile
      );
      const latestAlertErrors = getDefaultAlertErrors(latestFileModels);

      onRemoveFileListItem?.(removedFile);
      onChange(latestFileModels, latestAlertErrors);
    },
    [onChange, onRemoveFileListItem, getDefaultAlertErrors]
  );

  const alertId = alertErrors.length > 0 ? `${rootId}-alert` : undefined;
  const dropzoneDescriptorIds = [alertId, descriptionTextId].filter(
    (id) => id !== undefined
  );

  return (
    <FileUploaderFieldsetRoot
      {...slotProps.fieldset}
      id={rootId}
      className={clsx(
        slotProps.fieldset?.className,
        fileUploaderClasses.fieldset,
        disabled && fileUploaderClasses.disabled
      )}
      spacing={2}
      // @ts-expect-error as long as component is set to fieldset, this is fine.
      ref={forwardedRef}
      component={'fieldset'}
      aria-label={titleText}
      aria-live="polite"
    >
      {!(hideTitleText && hideDescriptionText) && (
        <FileUploaderLegend
          {...slotProps.legend}
          descriptionTextId={descriptionTextId}
          className={slotProps.legend?.className}
          titleText={titleText}
          hideTitleText={hideTitleText}
          descriptionText={descriptionText}
          hideDescriptionText={hideDescriptionText}
        />
      )}
      {showDropzone && (
        <FileUploaderDropzone
          {...slotProps.dropzone}
          aria-describedby={
            dropzoneDescriptorIds.length === 0
              ? undefined
              : dropzoneDescriptorIds.join(' ')
          }
          disabled={disabled}
          onChange={handleChange}
          acceptedMimeTypes={acceptedMimeTypes}
          minSizeInBytes={minSizeInBytes}
          maxSizeInBytes={maxSizeInBytes}
          maxNumberOfFiles={maxNumberOfFiles}
        />
      )}
      {alertErrors.length === 0 ? undefined : (
        <FileUploaderAlert
          id={alertId}
          errors={alertErrors}
          onClose={
            maxNumberOfFiles === 1
              ? () => {
                  // When only a single file is supported, and there is an alert
                  // error present, clearing the error should "reset" the component
                  // into a state where `fileValues` is empty, and our internal
                  // alert error handling is reset to remove the alert from the DOM.
                  getDefaultAlertErrors([]);
                  onChange([], []);
                }
              : undefined
          }
        />
      )}
      <FileUploaderFileList
        slotProps={{ fileList: slotProps.fileList ? slotProps.fileList : {} }}
        className={clsx(
          slotProps.fileList?.className,
          fileUploaderClasses.fileList
        )}
        actionFooterClassName={fileUploaderClasses.actionFooter}
        fileValues={shouldHideFileList ? [] : fileValuesProp}
        renderFileListItem={renderFileListItem}
        renderFileListItemActionMenuItems={renderFileListItemActionMenuItems}
        maxFileListItemsToDisplay={maxFileListItemsToDisplay}
        maxFileListContainerHeight={maxFileListContainerHeight}
        showFileSize={showFileSize}
        onRemoveFileListItem={handleRemoveFileListItem}
        onFileChange={onChange}
      />
    </FileUploaderFieldsetRoot>
  );
});

export default FileUploader;
